<template>
<div class="position-relative">
    <!-- Main Payment Method Card -->
    <v-card
        :class="{
            'elevation-2': value.is_active_payment_method,
            'grey lighten-4': value.is_archived
        }"
        :outlined="!value.is_active_payment_method"
        class="mb-3"
    >
        <v-card-text class="pa-3">
            <v-row no-gutters align="center">
                <!-- Card/Bank Icon -->
                <v-col cols="auto" class="mr-4">
                    <v-avatar size="48" color="grey lighten-4" v-if="card_image == null" class="mr-3">
                        <v-icon color="grey darken-1">mdi-bank</v-icon>
                    </v-avatar>
                    <v-img v-else :src="card_image" max-width="80" max-height="40" contain></v-img>
                </v-col>

                <!-- Payment Details -->
                <v-col>
                    <div v-if="value.payment_method_type == 'card'">
                        <div class="subtitle-1 font-weight-medium">{{value.customer_full_name}}</div>
                        <div class="body-2 grey--text text--darken-1">
                            <span class="font-weight-medium">••••&nbsp;{{value.card_ending}}</span>
                            <span class="mx-2">•</span>
                            <span>Expires {{value.card_exp_month}}/{{value.card_exp_year}}</span>
                        </div>
                    </div>

                    <div v-else>
                        <div class="subtitle-1 font-weight-medium">{{value.us_bank_account_bank_name}}</div>
                        <div class="body-2 grey--text text--darken-1">
                            <span class="font-weight-medium">••••&nbsp;{{value.us_bank_account_ending}}</span>
                        </div>
                    </div>

                    <div class="caption grey--text mt-1">
                        Added on {{$rc.pretty(value.time_created,'date')}}
                    </div>
                </v-col>

                <!-- Status Badges -->
                <v-col cols="auto" class="text-right d-flex align-center">
                    <!-- Active badge -->
                    <div v-if="value.is_active_payment_method">
                        <v-btn
                            small
                            color="success"
                            class="white--text active-status-btn"
                            aria-label="Active payment method"
                            @click.prevent
                        >
                            <v-icon left size="16">mdi-check</v-icon>
                            Active
                        </v-btn>
                    </div>


                    <!-- Archived badge and Restore button -->
                    <div v-if="value.is_archived" class="d-flex align-center">
                        <v-btn
                            color="grey darken-1"
                            small
                            class="white--text mr-2 archived-status-btn"
                            aria-label="Archived payment method"
                            @click.prevent
                        >
                            <v-icon left size="16">mdi-archive</v-icon>
                            Archived
                        </v-btn>

                        <v-btn
                            color="success"
                            small
                            outlined
                            @click="onClickRestorePaymentMethod"
                            aria-label="Restore payment method"
                        >
                            <v-icon left size="16">mdi-refresh</v-icon>
                            Unarchive
                        </v-btn>
                    </div>

                    <!-- Make Active and Archive buttons for non-archived, non-active methods -->
                    <div v-if="!value.is_active_payment_method && !value.is_archived" >
                        <v-btn
                            small
                            outlined
                            color="grey darken-1"
                             class="mr-2"
                            @click="onClickArchivePaymentMethod"
                            aria-label="Archive payment method"
                        >
                            <v-icon left size="16">mdi-archive</v-icon>
                            Archive
                        </v-btn>
                        <v-btn
                            small
                            outlined
                            color="success"
                            @click="onClickPaymentMethod"
                            aria-label="Make payment method active"
                        >
                            <v-icon left size="16">mdi-check</v-icon>
                            Make Active
                        </v-btn>


                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</div>
</template>

<script>
export default {
    props: {
        value: {
            type: Object,
            default: function() { return {}; }
        },
    },

    methods: {
        onClickPaymentMethod()
        {
            if (this.value.is_active_payment_method || this.value.is_archived)
                return;

            this.$rc.confirm({
                'title': 'Change active payment method',
                'body': 'Make '+this.description+' your active RedChirp payment method?',
                'btn_text_ok': 'Yes',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doChangePaymentMethod();
                },
            });
        },

        doChangePaymentMethod()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/admin/red-chirp-payments/set-active-payment-method',

                'params' : {
                        'organization_id': this.value.linked_to_organization_id,
                        'payment_method_id': this.value.payment_method_id,
                    },

                'on_success': () => {
                        this.$emit('update');
                    },

                'on_error': 'Unable to change active payment method.',
            });
        },

        onClickArchivePaymentMethod(event) {
            event.stopPropagation();

            this.$rc.confirm({
                'title': 'Archive payment method',
                'body': 'Are you sure you want to archive ' + this.description + '? Archived payment methods will not be visible by default.',
                'btn_text_ok': 'Yes, archive it',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doArchivePaymentMethod();
                },
            });
        },

        doArchivePaymentMethod() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'DELETE: /api/v1/organizations/red-chirp-payments/payment-method',

                'params': {
                    'organization_id': this.value.linked_to_organization_id,
                    'payment_method_id': this.value.payment_method_id,
                    'payment_gateway_id': this.value.payment_gateway_id,
                },

                'on_success': (response) => {
                    if (response.success) {
                        this.$emit('update');
                    } else {
                        this.$rc.alert(
                            'Error',
                            response.error
                        );
                    }
                },

                'on_error': 'Unable to archive payment method.',
            });
        },

        onClickRestorePaymentMethod(event) {
            event.stopPropagation();

            this.$rc.confirm({
                'title': 'Restore payment method',
                'body': 'Are you sure you want to restore ' + this.description + '?',
                'btn_text_ok': 'Yes, restore it',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doRestorePaymentMethod();
                },
            });
        },

        doRestorePaymentMethod() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/organizations/red-chirp-payments/restore-payment-method',

                'params': {
                    'organization_id': this.value.linked_to_organization_id,
                    'payment_method_id': this.value.payment_method_id,
                    'payment_gateway_id': this.value.payment_gateway_id,
                },

                'on_success': (response) => {
                    if (response.success) {
                        this.$emit('update');
                    } else {
                        this.$rc.alert(
                            'Error',
                            response.error
                        );
                    }
                },

                'on_error': 'Unable to restore payment method.',
            });
        },
    },

    computed: {
        card_image()
        {
            if ( this.value.card_brand == null ) return null;

            if ( this.value.card_brand == 'amex' ) return '/app/payment-method-logos/american-express.svg';
            if ( this.value.card_brand == 'diners' ) return '/app/payment-method-logos/diners-club.svg';
            if ( this.value.card_brand == 'discover' ) return '/app/payment-method-logos/discover.svg';

            if ( this.value.card_brand == 'jcb' ) return '/app/payment-method-logos/jcb.svg';
            if ( this.value.card_brand == 'mastercard' ) return '/app/payment-method-logos/master-card.svg';
            if ( this.value.card_brand == 'unionpay' ) return '/app/payment-method-logos/unionpay.svg';

            if ( this.value.card_brand == 'visa' ) return '/app/payment-method-logos/visa.svg';

            return '/app/payment-method-logos/generic.svg';
        },

        description()
        {
            if ( this.value.card_brand != null )
            {
                return 'your '+this.value.card_brand+ (this.value.card_ending != null ? ' ending in '+this.value.card_ending : '')
            }

            return 'your bank account with '+this.value.us_bank_account_bank_name +' ending in '+this.value.us_bank_account_ending;
        },
    },
}
</script>

<style lang="scss" scoped>
.position-relative {
  position: relative;
}

.active-status-btn,
.archived-status-btn {
  cursor: default !important;
  opacity: 1 !important;
}

.active-status-btn {
  background-color: var(--v-success-base) !important;
}

.archived-status-btn {
  background-color: var(--v-grey-darken1) !important;
}
</style>
